import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../../config';
import { apiRequest } from '../../../utils/apiUtils';
import { useVenueAvailability, getDayName } from '../../../hooks/venue/useVenueAvailability';
import DayAvailabilityRow from '../../../components/venue/DayAvailabilityRow';

interface Venue {
  id: string;
  name: string;
  type: string;
}

const VenueAvailabilityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loadingVenue, setLoadingVenue] = useState(true);

  const {
    availability,
    isLoading,
    isSaving,
    error,
    saveAvailability,
    toggleDay,
    addBlock,
    removeBlock,
    updateBlock,
    validateAvailability,
  } = useVenueAvailability(id);

  // Fetch venue details
  useEffect(() => {
    if (!id) return;

    const fetchVenue = async () => {
      try {
        const result = await apiRequest<Venue>(`/api/venues/${id}`, {
          requireAuth: true,
        });

        if (result.data) {
          setVenue(result.data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching venue:', err);
      } finally {
        setLoadingVenue(false);
      }
    };

    fetchVenue();
  }, [id]);

  const handleSave = async () => {
    // Validate before saving
    const validationError = validateAvailability();
    if (validationError) {
      alert(validationError);
      return;
    }

    const success = await saveAvailability();
    if (success) {
      alert('✅ Configuración guardada exitosamente');
      navigate(`/owner/venues/${id}/edit`);
    } else if (error) {
      alert(`❌ Error: ${error}`);
    }
  };

  const handleCancel = () => {
    navigate(`/owner/venues/${id}/edit`);
  };

  // Calculate summary stats
  const totalHoursPerWeek = availability.reduce((total, day) => {
    if (!day.enabled) return total;
    return (
      total +
      day.blocks.reduce((dayTotal, block) => {
        const start = parseInt(block.startTime.split(':')[0]);
        const end = parseInt(block.endTime.split(':')[0]);
        return dayTotal + (end - start);
      }, 0)
    );
  }, 0);

  const enabledDaysCount = availability.filter((day) => day.enabled).length;

  if (loadingVenue || isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#181A1B',
          color: '#FFFFFF',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 16 }}>⚽</div>
          <div>Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#181A1B',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: '#23272A',
          borderBottom: '1px solid #33383B',
          padding: '32px 48px',
        }}
      >
        <button
          onClick={() => navigate('/owner/venues')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'none',
            border: 'none',
            color: '#00E676',
            fontSize: 14,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            cursor: 'pointer',
            marginBottom: 12,
            padding: 0,
          }}
        >
          <span style={{ fontSize: 20 }}>←</span>
          Volver a canchas
        </button>

        <h1
          style={{
            fontSize: 36,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            letterSpacing: 0.5,
            color: '#00E676',
            margin: 0,
            marginBottom: 8,
          }}
        >
          CONFIGURAR HORARIOS Y PRECIOS
        </h1>

        <p
          style={{
            fontSize: 14,
            color: '#B0BEC5',
            margin: 0,
          }}
        >
          {venue?.name} - {venue?.type} - Define los horarios disponibles y precios para esta cancha
        </p>
      </div>

      {/* Main Content */}
      <div
        style={{
          display: 'flex',
          gap: 32,
          padding: '32px 48px',
        }}
      >
        {/* Schedule Section */}
        <div style={{ flex: 1, maxWidth: 820 }}>
          <div
            style={{
              backgroundColor: '#23272A',
              border: '1px solid #33383B',
              borderRadius: 16,
              padding: 24,
            }}
          >
            <h2
              style={{
                fontSize: 24,
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                letterSpacing: 0.5,
                color: '#00E676',
                margin: 0,
                marginBottom: 8,
              }}
            >
              HORARIOS DISPONIBLES
            </h2>
            <p
              style={{
                fontSize: 14,
                color: '#B0BEC5',
                margin: 0,
                marginBottom: 24,
              }}
            >
              Configura los días y horarios en que tu cancha está disponible para reservas
            </p>

            {/* Days */}
            <div>
              {availability.map((day) => (
                <DayAvailabilityRow
                  key={day.dayOfWeek}
                  dayName={getDayName(day.dayOfWeek)}
                  dayOfWeek={day.dayOfWeek}
                  enabled={day.enabled}
                  blocks={day.blocks}
                  onToggleDay={toggleDay}
                  onAddBlock={addBlock}
                  onRemoveBlock={removeBlock}
                  onUpdateBlock={updateBlock}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Summary & Pricing */}
        <div style={{ width: 400 }}>
          {/* Summary Card */}
          <div
            style={{
              backgroundColor: '#23272A',
              border: '2px solid #00E676',
              borderRadius: 16,
              padding: 24,
              marginBottom: 24,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                letterSpacing: 0.5,
                color: '#00E676',
                margin: 0,
                marginBottom: 16,
              }}
            >
              RESUMEN
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 14,
                  color: '#B0BEC5',
                }}
              >
                <span>Días habilitados:</span>
                <span style={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {enabledDaysCount} de 7
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 14,
                  color: '#B0BEC5',
                }}
              >
                <span>Horas por semana:</span>
                <span style={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {totalHoursPerWeek}h
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 14,
                  color: '#B0BEC5',
                }}
              >
                <span>Promedio horas/día:</span>
                <span style={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {enabledDaysCount > 0
                    ? (totalHoursPerWeek / enabledDaysCount).toFixed(1)
                    : 0}
                  h
                </span>
              </div>
            </div>

            {enabledDaysCount === 0 && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  backgroundColor: '#FF525220',
                  border: '1px solid #FF5252',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#FF5252',
                }}
              >
                ⚠️ Debes habilitar al menos un día
              </div>
            )}
          </div>

          {/* Info Card */}
          <div
            style={{
              backgroundColor: '#23272A',
              border: '1px solid #33383B',
              borderRadius: 16,
              padding: 20,
            }}
          >
            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#FFFFFF',
                margin: 0,
                marginBottom: 12,
              }}
            >
              💡 Consejos
            </h4>

            <ul
              style={{
                margin: 0,
                padding: 0,
                paddingLeft: 20,
                fontSize: 13,
                color: '#B0BEC5',
                lineHeight: 1.6,
              }}
            >
              <li>Marca los horarios pico para aplicar precios especiales</li>
              <li>Puedes agregar múltiples bloques por día</li>
              <li>Los usuarios solo verán horarios disponibles</li>
              <li>
                Actualiza tus horarios cuando sea necesario
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          backgroundColor: '#23272A',
          borderTop: '1px solid #33383B',
          padding: '24px 48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button
          onClick={handleCancel}
          disabled={isSaving}
          style={{
            padding: '16px 40px',
            backgroundColor: '#23272A',
            border: '1px solid #33383B',
            borderRadius: 12,
            color: '#B0BEC5',
            fontSize: 16,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            letterSpacing: 0.5,
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.5 : 1,
          }}
        >
          CANCELAR
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving || enabledDaysCount === 0}
          style={{
            padding: '16px 60px',
            backgroundColor: enabledDaysCount === 0 ? '#33383B' : '#00E676',
            border: 'none',
            borderRadius: 12,
            color: '#181A1B',
            fontSize: 16,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            letterSpacing: 0.5,
            cursor: isSaving || enabledDaysCount === 0 ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
          }}
        >
          {isSaving ? 'GUARDANDO...' : 'GUARDAR CONFIGURACIÓN'}
        </button>
      </div>

      {error && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: '#FF5252',
            color: '#FFFFFF',
            padding: '16px 20px',
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default VenueAvailabilityPage;
